import { type UseQueryResult, keepPreviousData } from '@tanstack/react-query';
import omitProperties from 'lodash/omit';
import pickProperties from 'lodash/pick';
import { type Dispatch, type MutableRefObject, useContext } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import Enums from '@core/types/api/Enums';
import type { ExcludeFromUnion, NestedKeyOf, Path } from '@core/utils/types';

import { useSlabQuery, useSlabQueryFetcher } from '../../hooks/useSlabQuery';
import type { FilterByOpts, ListURLParams, QueryRouteBarrelTypes } from '../../utils/ApiClient';
import { HashSet } from '../../utils/HashSet';
import {
  MakeImmediateResult,
  MergeAsyncResults,
  type PossiblyAsyncResult,
  type QueryError,
} from '../../utils/Query';
import { useAsyncReducer } from '../../utils/useAsyncReducer';
import type { SortConfig } from './components/SortHelpers';
import {
  type Column,
  type ColumnConfig,
  HashFilter,
  type TableDataModel,
  columnFromConfig,
  computeSortAfterFilterChange,
  reorderColumnMap,
} from './TableDataModel';

export const DEFAULT_QUERY_PARAMS = { page: 0, perPage: 50 };

export interface ReadableSet<T> {
  has(value: T): boolean;
  forEach(cb: (v: T) => void): void;
  [Symbol.iterator](): IterableIterator<T>;
  readonly size: number;
}

export type ColumnState<TRow extends { id: string }> = {
  readonly allColumnsByID: ReadonlyMap<string, Column<TRow>>;
  readonly renderedColumns: ReadableSet<Column<TRow>>;
};
type ColumnStateChangeAction<TRow extends { id: string }> = {
  columnID: NestedKeyOf<TRow>;
  isRendered: boolean;
};

export type ColumnAction<TRow extends { id: string }> =
  | {
      type: 'state';
      state: ColumnStateChangeAction<TRow>;
    }
  | {
      type: 'configs';
      columns: ColumnConfig<TRow>[];
    }
  | {
      type: 'set_column_order';
      columnIDs: NestedKeyOf<TRow>[];
    };

const hashColumn = <TRow extends { id: string }>(column: unknown): string => {
  const col = column as Column<TRow>;
  return col.id;
};

export const ApiTableDataImplementation = <
  TRow extends { id: string },
  RowArgs extends { queryParams: ListURLParams<TRow> },
  TAgg extends { id: string },
  TMore,
>(
  rowsResult: PossiblyAsyncResult<{ rows: TRow[]; count: number }>,
  aggregationsResult: PossiblyAsyncResult<{ [id: string]: TAgg }>,
  moreDataResult: PossiblyAsyncResult<TMore>,
  fetchAllRows: () => Promise<TRow[]>,
  makeColumnConfigs: (moreData: TMore) => ColumnConfig<TRow>[],
  [queryArgs, queryArgsRef, setQueryArgs]: [RowArgs, MutableRefObject<RowArgs>, Dispatch<RowArgs>],
  setUpAsyncReducer: typeof useAsyncReducer<
    ColumnState<TRow>,
    ColumnAction<TRow>,
    ColumnConfig<TRow>[]
  >,
  hasPermissions: (requiredPermissions?: Enums.Permission[]) => boolean,
  initialSortBy?: ListURLParams<TRow>['sortBy'],
): TableDataModel<TRow, TAgg> => {
  const [columnData, columnDataRef, dispatchColumnChange] = setUpAsyncReducer(
    (state: ColumnState<TRow>, action: ColumnAction<TRow>): ColumnState<TRow> => {
      switch (action.type) {
        case 'state': {
          const renderedColumnIDs = new Set([...state.renderedColumns].map((col) => col.id));

          if (action.state.isRendered) {
            renderedColumnIDs.add(action.state.columnID);
          } else {
            renderedColumnIDs.delete(action.state.columnID);
          }

          const allColumnsByID = new Map(state.allColumnsByID);
          allColumnsByID.forEach((col, id) => {
            allColumnsByID.set(id, {
              ...col,
              isDisplayed: renderedColumnIDs.has(id as Path<TRow>),
            });
          });

          return {
            allColumnsByID,
            renderedColumns: new HashSet(
              hashColumn,
              [...allColumnsByID.values()].filter((col) => renderedColumnIDs.has(col.id)),
            ),
          };
        }
        case 'configs': {
          const allColumnsByID = new Map(
            action.columns.map(columnFromConfig).map((c) => [c.id, c]),
          );
          return {
            allColumnsByID,
            renderedColumns: new HashSet(
              hashColumn,
              [...state.renderedColumns].filter((c) => allColumnsByID.has(c.id)),
            ),
          };
        }
        case 'set_column_order': {
          const reordered = reorderColumnMap(state.allColumnsByID, action.columnIDs);
          return {
            allColumnsByID: reordered,
            renderedColumns: new HashSet(
              hashColumn,
              [...reordered.values()].filter((col) => state.renderedColumns.has(col)),
            ),
          };
        }
        default:
          // do nothing
          return state;
      }
    },
    makeColumnConfigs(moreDataResult.data),
    (configs: ColumnConfig<TRow>[]): ColumnState<TRow> => {
      const allColumns = configs.map(columnFromConfig);
      const renderedColumns = configs
        .filter((config) => config.isDisplayed ?? false)
        .map((config) => allColumns.find((c) => c.id === config.id)) as Column<TRow>[];
      return {
        allColumnsByID: new Map(allColumns.map((c) => [c.id, c])),
        renderedColumns: new HashSet(hashColumn, renderedColumns),
      };
    },
  );
  // Update active column list if it has changed.
  if (!moreDataResult.isLoading) {
    const configs = makeColumnConfigs(moreDataResult.data);
    // When the moreDataResult has loaded, if the set of column configs produced by their factory has changed, then
    // asynchronously update the set of columns.  This is asynchronous to defer the filter change until immediately
    // after the current rendering pass finishes, because React assumes this change might affect the containing node's
    // rendering and that containing node has already been (partially) rendered by the time we get here.
    if (configs.map((c) => c.id).some((id) => !columnDataRef.current.allColumnsByID.has(id))) {
      dispatchColumnChange({ type: 'configs', columns: configs });
    }
  }

  return {
    isLocallyProcessed: true,
    get rowCount(): PossiblyAsyncResult<number> {
      // HACK: if we fail to load a page, return a total that includes the current page. Otherwise the MUI pagination
      // component displays "0-0 of 0" and only allows navigation back to the very first page, which is super
      // annoying if the HTTP request for a much later page errors out.
      if (rowsResult.isError && queryArgs.queryParams.page > 0) {
        const count = queryArgs.queryParams.perPage * (queryArgs.queryParams.page + 1);
        return { ...rowsResult, data: count };
      }
      return { ...rowsResult, data: rowsResult.data.count };
    },
    get allColumns(): ReadableSet<Column<TRow>> {
      return new Set(
        [...columnData.allColumnsByID.values()].filter((c) => hasPermissions(c.permissions)),
      );
    },
    get renderedColumns(): ReadableSet<Column<TRow>> {
      return new HashSet(hashColumn, [...columnData.renderedColumns]);
    },
    async setIsColumnRendered(columnID: NestedKeyOf<TRow>, isRendered: boolean): Promise<void> {
      await dispatchColumnChange({ type: 'state', state: { columnID, isRendered } });
    },
    async setColumnOrder(columnIDs: NestedKeyOf<TRow>[]): Promise<void> {
      await dispatchColumnChange({ type: 'set_column_order', columnIDs });
    },
    get aggregations(): PossiblyAsyncResult<{ [id: string]: TAgg }> {
      return aggregationsResult;
    },
    get pageIndex(): number {
      return queryArgs.queryParams.page ?? 0;
    },
    get currentPage(): PossiblyAsyncResult<TRow[]> {
      return { ...rowsResult, data: rowsResult.data.rows };
    },
    async setPageIndex(pageIndex: number): Promise<void> {
      const args = queryArgsRef.current;
      await setQueryArgs({ ...args, queryParams: { ...args.queryParams, page: pageIndex } });
    },
    async getRowsForAllPagesWithPotentiallyPoorPerformance(): Promise<TRow[]> {
      return fetchAllRows();
    },
    get sortConfig(): SortConfig<TRow> {
      return (
        queryArgs.queryParams.sortBy ?? {
          direction: Enums.SortDirection.Ascending,
          name: [...this.renderedColumns][0]?.id ?? 'id',
        }
      );
    },
    get initialSortConfig(): SortConfig<TRow> {
      return (
        initialSortBy ?? {
          direction: Enums.SortDirection.Ascending,
          name: [...this.renderedColumns][0]?.id ?? 'id',
        }
      );
    },
    async setSortConfig(sortConfig: SortConfig<TRow>): Promise<void> {
      const { queryParams } = queryArgsRef.current;
      if (
        sortConfig.direction === queryParams.sortBy?.direction &&
        sortConfig.name === queryParams.sortBy?.name
      ) {
        return;
      }
      const args = queryArgsRef.current;
      await setQueryArgs({
        ...args,
        queryParams: { ...args.queryParams, sortBy: sortConfig, page: 0 },
      });
    },
    get rowCountPerPage(): number {
      return queryArgs.queryParams.perPage;
    },
    async setRowCountPerPage(rowCountPerPage: number): Promise<void> {
      const args = queryArgsRef.current;
      await setQueryArgs({
        ...args,
        queryParams: { ...args.queryParams, perPage: rowCountPerPage },
      });
    },
    getFilter(
      hashData: ExcludeFromUnion<FilterByOpts<TRow>, 'value'>,
    ): FilterByOpts<TRow> | undefined {
      const hash = HashFilter(hashData);
      const filterBy = queryArgs.queryParams.filterBy ?? [];
      return filterBy.find((filter) => hash === HashFilter(filter)) as
        | FilterByOpts<TRow>
        | undefined;
    },
    getColumnFilter(columnID: NestedKeyOf<TRow>): FilterByOpts<TRow> | undefined {
      const args = queryArgsRef.current;
      const originalFilterBy = args.queryParams.filterBy ?? [];
      return originalFilterBy.find((it) => 'name' in it && it.name === columnID);
    },
    getAllColumnFilters(): FilterByOpts<TRow>[] {
      const args = queryArgsRef.current;
      const originalFilterBy = args.queryParams.filterBy ?? [];
      return originalFilterBy;
    },
    /** Add filters, or update reference values for existing filters with the same operation and paths. */
    async addFilters(...filters: FilterByOpts<TRow>[]): Promise<void> {
      await this.replaceFilters([], filters);
    },
    /** Remove filters, matching on operation and path. */
    async removeFilters(...filters: FilterByOpts<TRow>[]): Promise<void> {
      await this.replaceFilters(filters, []);
    },
    async clearFilters(): Promise<void> {
      const args = queryArgsRef.current;
      const originalFilterBy = args.queryParams.filterBy ?? [];
      const nextSort = computeSortAfterFilterChange(
        this.allColumns,
        originalFilterBy,
        [],
        args.queryParams.sortBy ?? this.initialSortConfig,
        this.initialSortConfig,
      );
      setQueryArgs({
        ...args,
        queryParams: { ...args.queryParams, filterBy: [], sortBy: nextSort, page: 0 },
      });
    },
    async setFilters(...filterBy: FilterByOpts<TRow>[]): Promise<void> {
      const args = queryArgsRef.current;
      const originalFilterBy = args.queryParams.filterBy ?? [];
      const nextSort = computeSortAfterFilterChange(
        this.allColumns,
        originalFilterBy,
        filterBy,
        args.queryParams.sortBy ?? this.initialSortConfig,
        this.initialSortConfig,
      );
      setQueryArgs({
        ...args,
        queryParams: { ...args.queryParams, filterBy, sortBy: nextSort, page: 0 },
      });
    },
    async replaceFilters(
      oldFilters: FilterByOpts<TRow>[],
      newFilters: FilterByOpts<TRow>[],
    ): Promise<void> {
      const args = queryArgsRef.current;
      const originalFilterBy = args.queryParams.filterBy ?? [];
      const filterBy = new HashSet<FilterByOpts<TRow>>(HashFilter<TRow>, [
        ...originalFilterBy,
      ] as FilterByOpts<TRow>[]);
      oldFilters.forEach((f) => {
        filterBy.delete(f);
      });
      newFilters.forEach((f) => {
        filterBy.add(f);
      });
      // Don't set new filters if the newly computed filters exactly match the previous ones.
      // In addition to being a performance optimization (skipping unnecessary React rendering passes),
      // this avoids infinite loops given the DataTable's current approach to managing tab filters.
      if (originalFilterBy.length === filterBy.size) {
        const hasChanges = originalFilterBy.some((original) => {
          const updated = filterBy.get(original);
          if (updated === undefined) {
            return true;
          }
          return Object.entries(original).some(
            ([k, v]) => v !== updated[k as keyof FilterByOpts<TRow>],
          );
        });
        if (!hasChanges) {
          return;
        }
      }
      const nextFilters = [...filterBy];
      const nextSort = computeSortAfterFilterChange(
        this.allColumns,
        originalFilterBy,
        nextFilters,
        args.queryParams.sortBy ?? this.initialSortConfig,
        this.initialSortConfig,
      );
      await setQueryArgs({
        ...args,
        queryParams: {
          ...args.queryParams,
          filterBy: nextFilters,
          sortBy: nextSort,
          page: 0,
        },
      });
    },
  };
};

export type ApiTableDataWithMoreParams<
  TRow extends { id: string },
  RowKey extends keyof QueryRouteBarrelTypes,
  RowBarrel extends QueryRouteBarrelTypes[RowKey],
  TAgg extends { id: string },
  AggKey extends keyof QueryRouteBarrelTypes,
  AggBarrel extends QueryRouteBarrelTypes[AggKey],
  TMore,
> = {
  rowsRouteKey: RowKey;
  rowsQueryOptions?: RowBarrel extends { options: {} } ? RowBarrel['options'] : any;
  initialSortBy: ListURLParams<TRow>['sortBy'];
  /** @default 50 */
  initialPerPage?: number;
  aggregationsRouteKey: AggKey;
  initialFilters?: ListURLParams<TRow>['filterBy'];
  aggregationsQueryOptions?: AggBarrel extends { options: {} } ? AggBarrel['options'] : any;
  moreDataResult: PossiblyAsyncResult<TMore>;
  extractRows: (data: RowBarrel['returns'], moreData: TMore) => TRow[];
  extractAggregations: (data: AggBarrel['returns']) => { [id: string]: TAgg };
  makeColumnConfigs: (moreData: TMore) => ColumnConfig<TRow>[];
  makeAggregationArgs?: (rowsQueryArgs: RowBarrel['args']) => AggBarrel['args'];
} & (RowBarrel['args'] extends { pathParams: {} }
  ? { rowsPathParams: RowBarrel['args']['pathParams'] }
  : { rowsPathParams?: any });
export type ApiTableDataParams<
  TRow extends { id: string },
  RowKey extends keyof QueryRouteBarrelTypes,
  RowBarrel extends QueryRouteBarrelTypes[RowKey],
  TAgg extends { id: string },
  AggKey extends keyof QueryRouteBarrelTypes,
  AggBarrel extends QueryRouteBarrelTypes[AggKey],
> = Omit<
  ApiTableDataWithMoreParams<TRow, RowKey, RowBarrel, TAgg, AggKey, AggBarrel, undefined>,
  'moreDataResult'
>;
export type ApiTableDataWithoutAggregationsParams<
  TRow extends { id: string },
  RowKey extends keyof QueryRouteBarrelTypes,
  RowBarrel extends QueryRouteBarrelTypes[RowKey],
> = Omit<
  ApiTableDataWithMoreParams<TRow, RowKey, RowBarrel, any, RowKey, RowBarrel, undefined>,
  | 'moreDataResult'
  | 'aggregationsRouteKey'
  | 'aggregationsQueryOptions'
  | 'extractAggregations'
  | 'makeAggregationArgs'
>;

const generateQueryParams = <TRow extends { id: string }>(
  sortBy: ListURLParams<TRow>['sortBy'],
  filterBy: ListURLParams<TRow>['filterBy'] = [],
  perPage?: number,
): ListURLParams<TRow> => {
  const params: ListURLParams<TRow> = {
    ...DEFAULT_QUERY_PARAMS,
    sortBy,
  };
  if (perPage !== undefined) {
    params.perPage = perPage;
  }
  if (filterBy.length > 0) {
    params.filterBy = filterBy;
  }
  return params;
};

export const useApiTableDataWithMore = <
  TRow extends { id: string },
  RowKey extends keyof QueryRouteBarrelTypes,
  RowBarrel extends QueryRouteBarrelTypes[RowKey] & { returns: { count: number } },
  TAgg extends { id: string },
  AggKey extends keyof QueryRouteBarrelTypes,
  AggBarrel extends QueryRouteBarrelTypes[AggKey],
  TMore,
>({
  rowsRouteKey,
  rowsPathParams = {},
  initialSortBy,
  initialFilters = [],
  aggregationsRouteKey,
  moreDataResult,
  extractRows,
  extractAggregations,
  makeColumnConfigs,
  makeAggregationArgs = (rowsQueryArgs): AggBarrel['args'] =>
    pickProperties(rowsQueryArgs, 'queryParams.filterBy'),
  ...optionalParams
}: ApiTableDataWithMoreParams<
  TRow,
  RowKey,
  RowBarrel,
  TAgg,
  AggKey,
  AggBarrel,
  TMore
>): TableDataModel<TRow, TAgg> => {
  type RowArgsWithQueryParams = RowBarrel['args'] & {
    queryParams: ListURLParams<TRow>;
  };

  const ctx = useContext(SlabContext);

  const mergeQueryParams = <T extends ListURLParams<TRow>>(
    prev: T,
    next: ListURLParams<TRow>,
  ): T => {
    const merged = { ...prev, ...next };
    if ((merged.filterBy ?? []).length === 0) {
      delete merged.filterBy;
    }
    return merged as T;
  };

  const reducerFn: (
    previousArgs: RowArgsWithQueryParams,
    nextArgs: RowBarrel['args'] & { queryParams: ListURLParams<TRow> },
  ) => RowArgsWithQueryParams = (previousArgs, nextArgs) => {
    return {
      ...previousArgs,
      ...nextArgs,
      queryParams: mergeQueryParams(previousArgs.queryParams, nextArgs.queryParams),
    };
  };

  const [queryArgs, queryArgsRef, setQueryArgs] = useAsyncReducer(
    reducerFn,
    {
      pathParams: rowsPathParams,
      queryParams: generateQueryParams(initialSortBy, initialFilters),
    },
    (initialArgs: RowArgsWithQueryParams): RowArgsWithQueryParams => initialArgs,
  );

  const rawRowResult: UseQueryResult<RowBarrel['returns'], QueryError> = useSlabQuery(
    rowsRouteKey,
    queryArgs,
    {
      ...optionalParams.rowsQueryOptions,
      placeholderData: keepPreviousData,
    },
  );

  const rowsResult: PossiblyAsyncResult<{ rows: TRow[]; count: number }> = {
    ...MergeAsyncResults({ rawRowResult, moreDataResult }),
    data: {
      rows:
        rawRowResult.data === undefined ||
        (moreDataResult.data === undefined && moreDataResult.status !== 'success')
          ? []
          : extractRows(rawRowResult.data, moreDataResult.data),
      count: rawRowResult.data?.count ?? 0,
    },
  };

  const rawAggregationResult = useSlabQuery(aggregationsRouteKey, makeAggregationArgs(queryArgs), {
    ...optionalParams.aggregationsQueryOptions,
    placeholderData: keepPreviousData,
  });
  const aggregationsResult: PossiblyAsyncResult<{ [id: string]: TAgg }> = {
    ...rawAggregationResult,
    data:
      rawAggregationResult.data === undefined ? {} : extractAggregations(rawAggregationResult.data),
  };

  const fetchPromisedRows = useSlabQueryFetcher();
  const fetchAllRows = async (): Promise<TRow[]> => {
    const queryParams = omitProperties(queryArgs.queryParams, ['page', 'perPage']);
    const args: any = { ...queryArgs, queryParams };
    const data = (await fetchPromisedRows(
      rowsRouteKey,
      args,
      optionalParams.rowsQueryOptions,
    )) as RowBarrel['returns'];
    return extractRows(data, moreDataResult.data);
  };

  return ApiTableDataImplementation(
    rowsResult,
    aggregationsResult,
    moreDataResult,
    fetchAllRows,
    makeColumnConfigs,
    [queryArgs, queryArgsRef, setQueryArgs],
    /* setUpColReducer= */ useAsyncReducer<
      ColumnState<TRow>,
      ColumnAction<TRow>,
      ColumnConfig<TRow>[]
    >,
    ctx.userInfo.hasPermissions,
    initialSortBy,
  );
};

export const useApiTableData = <
  TRow extends { id: string },
  RowKey extends keyof QueryRouteBarrelTypes,
  RowBarrel extends QueryRouteBarrelTypes[RowKey] & { returns: { count: number } },
  TAgg extends { id: string },
  AggKey extends keyof QueryRouteBarrelTypes,
  AggBarrel extends QueryRouteBarrelTypes[AggKey],
>(
  input: ApiTableDataParams<TRow, RowKey, RowBarrel, TAgg, AggKey, AggBarrel>,
): TableDataModel<TRow, TAgg> =>
  useApiTableDataWithMore({
    ...input,
    moreDataResult: MakeImmediateResult(undefined),
  } as ApiTableDataWithMoreParams<TRow, RowKey, RowBarrel, TAgg, AggKey, AggBarrel, undefined>);

export const useApiTableDataWithoutAggregations = <
  TRow extends { id: string },
  RowKey extends keyof QueryRouteBarrelTypes,
  RowBarrel extends QueryRouteBarrelTypes[RowKey] & {
    returns: { count: number };
    options: { enabled: boolean };
  },
>(
  input: ApiTableDataWithoutAggregationsParams<TRow, RowKey, RowBarrel>,
): TableDataModel<TRow, { id: string }> =>
  useApiTableDataWithMore({
    ...input,
    moreDataResult: MakeImmediateResult(undefined),
    aggregationsRouteKey: input.rowsRouteKey,
    aggregationsQueryOptions: {
      ...(input.rowsQueryOptions ?? {}),
      enabled: false,
    } as RowBarrel['options'],
    extractAggregations: () => ({}),
  } as ApiTableDataWithMoreParams<TRow, RowKey, RowBarrel, any, RowKey, RowBarrel, undefined>);
